import { useSelector } from "react-redux";
import { useCallback } from "react";

// Last resort when a key has no translation: the dashboard paints ~800ms before
// the translations request lands, and a raw key ("welcome_dashboard") on screen
// looks broken. Render the humanized key — "Welcome dashboard" — instead.
const humanize = (key) => {
  const leaf = String(key).split(".").pop() || String(key);
  const words = leaf.replace(/[_-]+/g, " ").trim();
  return words ? words.charAt(0).toUpperCase() + words.slice(1) : key;
};

const useTranslation = () => {
  const {
    translations,
    selectedLanguage,
    selectedLanguageCode,
    loadingTranslations,
  } = useSelector((state) => state.language);

  const t = useCallback(
    (key, fallback, vars) => {
      if (!key) return "";

      // Support dot notation: "errors.required"
      const raw = key
        .split(".")
        .reduce(
          (obj, part) =>
            obj && typeof obj === "object" ? obj[part] : undefined,
          translations,
        );

      let result =
        typeof raw === "string"
          ? raw
          : typeof fallback === "string"
            ? fallback
            : humanize(key);

      // {{varName}} interpolation
      if (vars && typeof vars === "object") {
        result = result.replace(/\{\{?(\w+)\}?\}?/g, (_, varName) =>
          Object.prototype.hasOwnProperty.call(vars, varName)
            ? String(vars[varName])
            : `{{${varName}}}`,
        );
      }

      return result;
    },
    [translations],
  );

  return {
    t,
    language: selectedLanguage,
    languageCode: selectedLanguageCode,
    isLoading: loadingTranslations,
  };
};

export default useTranslation;
